import { useState, type FC, type ReactNode } from "react";
import { Close } from "@mui/icons-material";
import { IconButton, Link, Typography } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";

import { mapTheme } from "modules/advancedMap/styles/AdvMapThemeProvider";

// Shared error parsing logic
const parseErrorData = (
  error: unknown,
): { Message?: string; RequestID?: string } | undefined => {
  if (error instanceof Error) {
    try {
      const parsed = JSON.parse(error.message) as unknown;
      if (
        parsed &&
        typeof parsed === "object" &&
        ("data" in parsed || "response" in parsed)
      ) {
        const parsedObj = parsed as {
          data?: { Message?: string; RequestID?: string };
          response?: { data?: { Message?: string; RequestID?: string } };
        };
        if (parsedObj.data) {
          return parsedObj.data;
        } else if (parsedObj.response?.data) {
          return parsedObj.response.data;
        }
      }
    } catch {
      // If not JSON, check if it's an AxiosError-like object
      const axiosError = error as {
        response?: { data?: { Message?: string; RequestID?: string } };
      };
      if (axiosError.response?.data) {
        return axiosError.response.data;
      }
    }
  } else if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as {
      response?: { data?: { Message?: string; RequestID?: string } };
    };
    if (axiosError.response?.data) {
      return axiosError.response.data;
    }
  }
  return undefined;
};

// Shared error display logic
const displayError = (
  errorData:
    | { Message?: string; Details?: string; RequestID?: string }
    | undefined,
  enqueueSnackbar: ReturnType<typeof useSnackbar>["enqueueSnackbar"],
  closeSnackbar: ReturnType<typeof useSnackbar>["closeSnackbar"],
  intl: ReturnType<typeof useIntl>,
) => {
  const emailMessage = intl.formatMessage(
    {
      id: "REQUEST.ERROR.MESSAGE",
    },
    {
      email: (
        <Link
          variant="body2"
          href={`mailto:insight@plugport.no?subject=Request ID (${errorData?.RequestID || ""})`}
          color={mapTheme.palette.text.hint}
        >
          insight@plugport.no
        </Link>
      ),
    },
  );

  enqueueSnackbar(
    <Typography variant="body2">
      {errorData?.Details ||
        errorData?.Message ||
        intl.formatMessage({ id: "GLOBAL.ERROR_MESSAGE" })}
      {errorData?.RequestID && ` (Request ID: ${errorData.RequestID}).`}
      <br />
      {errorData?.RequestID && emailMessage}
    </Typography>,
    {
      variant: "error",
      action: (key) => (
        <SnackbarActionIcon
          action={() => {
            closeSnackbar(key);
          }}
        />
      ),
      key: "error",
      autoHideDuration: 10000,
    },
  );
};

const ReactQueryProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const intl = useIntl();

  const handleError = (error: unknown) => {
    // Skip 401 errors (handled by redirect)
    if (error && typeof error === "object" && "response" in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 401) {
        return;
      }
    }

    const errorData = parseErrorData(error);
    displayError(errorData, enqueueSnackbar, closeSnackbar, intl);
  };

  // Create QueryClient once with error handler
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: import.meta.env.PROD,
          },
          mutations: {
            onError: handleError,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.DEV && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
};

export default ReactQueryProvider;

export const SnackbarActionIcon = ({ action }: { action: () => void }) => (
  <IconButton aria-label="delete" onClick={action}>
    <Close fontSize="small" />
  </IconButton>
);

export const useDefaultError = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const intl = useIntl();

  const handleError = (error: {
    data?: { Message?: string; Details?: string; RequestID?: string };
  }) => {
    displayError(error.data, enqueueSnackbar, closeSnackbar, intl);
  };

  return handleError;
};
